import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useState, useEffect } from "react";
import { Box } from "../Box";
import { Flex } from "../Flex";
import { DateTime } from "luxon";
import theme from "../../theme";
import { useIndividualsList } from "../../data/useIndividualsList";
import { IndividualListItem } from "../../types/Individual";
import { PagedResponse } from "../../types/Common";
import { StyledLink } from "../Breadcrumbs";
import { CHECK_ICONS } from "../../utils/screeningChecks";
import success from "../../assets/card-success-icon.svg";
import inReview from "../../assets/card-in-review-icon.svg";
import pending from "../../assets/pending-icon.svg";
import { Image } from "../Image";
import { Loader } from "../Loader";
import { Paragraph } from "../Paragraph";
import { Tooltip } from "../Tooltip";
import { capitalise } from "../../utils/string";
import {
  useServerTable,
  TablePagination,
  SortIndicator,
  pageCountFor,
} from "../ServerTable";
import { SubjectNameCell } from "../SubjectNameCell";
import { useFillViewportHeight } from "../../hooks/useFillViewportHeight";
// import { NoScopeSelectedNotice } from "../NoScopeSelectedNotice";

export const ChecksTable = (props) => {
  const {
    searchTerm,
    filterUserIds,
    filters,
    hasFilterChips,
    type,
    initialSorting,
    initialPageSize,
    onSortChange,
    onPageSizeChange,
  } = props;

  // Fill the space left below the page chrome, reserving room for the
  // pagination row that renders after the table.
  const { ref: tableRef, height: tableHeight } = useFillViewportHeight({
    bottomGutter: 80,
    remeasureKey: hasFilterChips,
  });

  const [data, setData] = useState<Partial<PagedResponse<IndividualListItem[]>>>({});
  const [filtersUpdate, setFiltersUpdate] = useState({ ...filters });
  const [searchTermUpdate, setSearchTermUpdate] = useState(props.searchTerm);
  const [filterUserIdsUpdate, setFilterUserIdsUpdate] = useState(
    props.filterUserIds
  );
  const [checkType] = useState(type);

  const checksIcons = CHECK_ICONS;

  let cols: ColumnDef<IndividualListItem>[] = [];

  cols = [
    {
      header: () => "Name",
      accessorKey:
        props.type === "entity"
          ? "company_name"
          : props.type === "individual"
          ? "first_name"
          : "first_name, company_name",
      cell: (info) => {
        const { company_name, first_name, last_name } = info.row.original;
        return (
          <SubjectNameCell
            type={props.type}
            companyName={company_name}
            firstName={first_name}
            lastName={last_name}
          />
        );
      },
    },

    {
      header: () => "Division",
      accessorKey: "primary_division",
      cell: (info) => {
        return (
          <Flex color={theme.colors.gray[40]}>
            {info.row.original.primary_division?.display_name ?? "-"}
          </Flex>
        );
      },
    },
    {
      header: () => "Team",
      accessorKey: "primary_team",
      cell: (info) => {
        return (
          <Flex color={theme.colors.gray[40]}>
            {info.row.original.primary_team?.display_name ?? "-"}
          </Flex>
        );
      },
    },
    {
      header: () => "Reference",
      accessorKey: "reference",
      cell: (info) => {
        return (
          <Flex
            justifyContent="start"
            alignItems="center"
            position="relative"
            color={theme.colors.gray[40]}
          >
            {info.getValue()}
          </Flex>
        );
      },
    },
    {
      header: () => "External Reference",
      accessorKey: "external_reference",
      size: 180,
      minSize: 180,
      maxSize: 180,
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue() || "-";

        return (
          <Flex
            justifyContent="start"
            alignItems="center"
            position="relative"
            color={theme.colors.gray[40]}
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={String(value)}
          >
            {String(value)}
          </Flex>
        );
      },
    },
    {
      header: () => "Checks",
      accessorKey: "checks",
      cell: (info) => {
        const { checks } = info.row.original;
        return (
          <Flex justifyContent="start" alignItems="center">
            {checks.map((check) => (
              <span key={check.id} style={{ margin: "0 5px" }}>
                <Tooltip content={capitalise(check.name.replace(/_/g, " "))}>
                  <Image
                    src={checksIcons[check.name]}
                    alt="checks"
                    height="auto"
                    width="22px"
                    mr={1}
                  />
                </Tooltip>
              </span>
            ))}
          </Flex>
        );
      },
    },
    {
      header: () => "Result",
      accessorKey: "result",
      cell: (info) => {
        const { checks_in_review, checks_completed, checks_in_pending } =
          info.row.original;
        return (
          <Flex justifyContent="start" alignItems="center">
            <Flex gap="6" mr={2} alignItems="center">
              <Tooltip content="Screening completed">
                <Image src={success} alt="success" height="20px" width="24px" />
              </Tooltip>
              {checks_completed}
            </Flex>
            <Flex gap="6" mr={2} alignItems="center">
              <Tooltip content="Screening in review">
                <Image
                  src={inReview}
                  alt="inReview"
                  height="20px"
                  width="24px"
                />
              </Tooltip>
              {checks_in_review}
            </Flex>
            <Flex gap="6" mr={2} alignItems="center">
              <Tooltip content="Screening pending">
                <Image src={pending} alt="pending" height="20px" width="24px" />
              </Tooltip>
              {checks_in_pending}
            </Flex>
          </Flex>
        );
      },
    },
    {
      header: () => "Created",
      accessorKey: "created_on",
      cell: (info) => {
        return (
          <Flex color={theme.colors.gray[40]}>
            {DateTime.fromISO(info.row.original.created_on).toFormat(
              "dd/MM/yyyy"
            )}
          </Flex>
        );
      },
    },
    {
      header: () => "",
      id: "view_id",
      accessorKey: "id",
      cell: (info) => {
        let path;
        const { id, mandate, has_address, checks_completed_date } =
          info.row.original;
        if (props.type === "entity" || info.row.original?.company_name) {
          path = `${mandate}/company/${id}`;
          if (!has_address) {
            path = `${mandate}/company/address/${id}`;
          } else if (checks_completed_date == null) {
            path = `${mandate}/company/run-screening/${id}`;
          }
        } else {
          path = `${mandate}/individuals/${id}`;
          if (!has_address) {
            path = `${mandate}/address/${id}`;
          } else if (checks_completed_date == null) {
            path = `${mandate}/run-screening/${id}`;
          }
        }

        // navigate(
        //   `/checks/${mandate.id}/company/address/${company.id}`,
        //   {
        //     state: {
        //       checkpointIds,
        //       isSanctionsSelected,
        //       isAdverseMediaSelected,
        //     },
        //   }
        // );

        return (
          <StyledLink to={path}>
            <span
              style={{
                color: theme.colors.blue.primary,
              }}
            >
              View
            </span>
          </StyledLink>
        );
      },
    },
  ];

  const [columns] = useState([...cols]);
  const {
    pageIndex,
    pageSize,
    pagination,
    setPagination,
    sorting,
    setSorting,
    sortOrder,
    sortBy,
    resetPagination,
  } = useServerTable(initialSorting, initialPageSize, onSortChange, onPageSizeChange);

  const getMandatesData = useIndividualsList(
    filterUserIdsUpdate,
    searchTermUpdate,
    filtersUpdate,
    pageIndex + 1,
    pageSize,
    sortOrder,
    sortBy,
    checkType
  );

  useEffect(() => {
    resetPagination();
    // if (filters) {
    setFiltersUpdate(filters);
    // }
    // if (searchTerm) {
    setSearchTermUpdate(searchTerm);
    // }
    // if (filterUserIds) {
    setFilterUserIdsUpdate(filterUserIds);
    // }
  }, [searchTerm, searchTermUpdate, filterUserIds, filters, resetPagination]);

  useEffect(() => {
    setData(() => getMandatesData?.data?.data);
  }, [getMandatesData]);

  const table = useReactTable({
    data: data?.results ?? [],
    columns,
    state: {
      pagination,
      sorting,
    },
    pageCount: pageCountFor(data?.count, pageSize),
    debugTable: true,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    manualPagination: true,
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div>
      {/* Hidden for now — see NoScopeSelectedNotice. */}
      {/* <NoScopeSelectedNotice /> */}
      {/* One fixed-height area for loading, empty and loaded states, so the
          pagination below stays in the footer whichever one is showing. It
          always renders, so it (not the table) carries the measuring ref. */}
      <Box ref={tableRef} height={tableHeight}>
        {getMandatesData.isFetching && (
          <Flex justifyContent="center" alignItems="center" height="100%">
            <Loader />
          </Flex>
        )}

        {!getMandatesData.isFetching && data?.results?.length === 0 && (
          <Paragraph m={3} color="gray.40" textAlign="center">
            No Screening found
          </Paragraph>
        )}

        {!getMandatesData.isFetching && data?.results?.length > 0 && (
          <div className="tableContainer" style={{ height: tableHeight }}>
            <table>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id} colSpan={header.colSpan}>
                        {header.isPlaceholder ? null : (
                          <>
                            <div
                              {...{
                                className: header.column.getCanSort()
                                  ? "select-none cursor-pointer flex items-center gap-1"
                                  : "",
                                onClick: header.column.getToggleSortingHandler(),
                              }}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              <SortIndicator column={header.column} />
                            </div>
                          </>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row, index) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Box>
      <TablePagination table={table} />
    </div>
  );
};
